/**
 * Continuous marquee that is also a hand-scrollable carousel.
 *
 * The strip is a real overflow scroller and the drift is applied by nudging
 * `scrollLeft` each frame, rather than by animating a transform. That choice is
 * what makes it draggable: a trackpad two-finger swipe, a touch flick with its
 * native momentum, and a mouse press-and-drag all move the same scroll offset,
 * so hand control and the drift never fight over one transform value. There are
 * no arrows — the strip is moved by hand or not at all.
 *
 * Cloning happens here rather than in Liquid because the markup has to stay
 * valid — a second `content_for 'blocks'` would repeat every block id in the
 * DOM. The clones are hidden from assistive tech so the list is announced once.
 *
 * Enough copies are made to cover the viewport plus a whole set of runway on
 * each side, so `#normalize` always has a set to teleport into: it keeps the
 * offset inside [setWidth, 2 * setWidth), which is visually identical to the
 * position it came from, so the loop has no seam in either direction.
 *
 * Speed is in pixels per second and independent of how many images there are.
 *
 * Without JavaScript nothing drifts and the strip is a normal scrollable row.
 */

/** Movement (px) that turns a press into a drag, and so cancels its click. */
const DRAG_THRESHOLD = 5;

/** How long the drift stays out of the way after a manual scroll (ms). */
const RESUME_DELAY = 1200;

/** Frame delta is clamped so returning to a background tab does not lurch. */
const MAX_FRAME_MS = 100;

class NoriMarquee extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {MutationObserver | undefined} */
  #itemObserver;
  /** @type {IntersectionObserver | undefined} */
  #visibilityObserver;
  /** @type {MediaQueryList | undefined} */
  #reduceMotion;
  /** @type {number | undefined} */
  #frame;
  /** @type {number | undefined} */
  #loop;

  /** Width of exactly one set of originals, including trailing gap. */
  #setWidth = 0;
  /** Sub-pixel drift that has not yet added up to a whole pixel. */
  #carry = 0;
  #lastTick = 0;
  /** Timestamp until which the drift yields to the person scrolling. */
  #idleUntil = 0;
  #onScreen = true;

  #dragging = false;
  #dragPointer = -1;
  #dragStartX = 0;
  #dragStartScroll = 0;
  /** Largest distance travelled during the current press. */
  #dragDistance = 0;

  connectedCallback() {
    this.#track = this.querySelector('[data-marquee-track]');
    if (!this.#track) return;

    this.#reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#build();

    this.#resizeObserver = new ResizeObserver(() => this.#schedule());
    this.#resizeObserver.observe(this);

    // Adding or removing an image in the theme editor has to re-measure.
    // Scoped to the track's direct children, and clones are marked so the
    // rebuild they cause does not trigger another one.
    this.#itemObserver = new MutationObserver((records) => {
      if (records.every((record) => this.#onlyClones(record))) return;
      this.#schedule();
    });
    this.#itemObserver.observe(this.#track, { childList: true });

    // A strip parked below the fold should not hold a frame callback open.
    this.#visibilityObserver = new IntersectionObserver((entries) => {
      this.#onScreen = entries.some((entry) => entry.isIntersecting);
      this.#onScreen ? this.#startLoop() : this.#stopLoop();
    });
    this.#visibilityObserver.observe(this);

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerEnd);
    this.addEventListener('pointercancel', this.#onPointerEnd);
    // Capture, so a drag that ended over an image never opens its link.
    this.addEventListener('click', this.#onClick, true);
    // The browser's own image drag would otherwise hijack the gesture.
    this.addEventListener('dragstart', this.#onDragStart);
    this.addEventListener('wheel', this.#yieldToHand, { passive: true });
    this.addEventListener('touchstart', this.#yieldToHand, { passive: true });

    this.#startLoop();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#itemObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#stopLoop();
  }

  /** @param {MutationRecord} record */
  #onlyClones(record) {
    const touched = [...record.addedNodes, ...record.removedNodes];
    return touched.every((node) => node instanceof Element && node.hasAttribute('data-marquee-clone'));
  }

  #schedule() {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#build());
  }

  get #originals() {
    return [...(this.#track?.children ?? [])].filter(
      (child) => !child.hasAttribute('data-marquee-clone')
    );
  }

  /** Pixels per second, or 0 when the drift is switched off. */
  get #speed() {
    if (this.#reduceMotion?.matches) return 0;
    const speed = Number.parseFloat(this.dataset.speed ?? '');
    return Number.isFinite(speed) && speed > 0 ? speed : 0;
  }

  #build() {
    const track = this.#track;
    if (!track) return;

    // Where we were within the current set, so a resize does not jump home.
    const offset = this.#setWidth ? (this.scrollLeft - this.#setWidth) % this.#setWidth : 0;

    for (const clone of track.querySelectorAll('[data-marquee-clone]')) clone.remove();

    const originals = this.#originals;
    if (originals.length === 0) {
      this.#setWidth = 0;
      this.removeAttribute('data-ready');
      return;
    }

    // Measured with the clones gone, so this is the width of exactly one set,
    // including the gap that follows the last item.
    const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
    const setWidth =
      originals.reduce((total, item) => total + item.getBoundingClientRect().width, 0) +
      gap * originals.length;

    if (setWidth <= 0) {
      this.#setWidth = 0;
      this.removeAttribute('data-ready');
      return;
    }

    // One set of runway on each side of the visible window, so the offset can
    // be wrapped in either direction without ever exposing an end.
    const copies = Math.max(3, Math.ceil(this.offsetWidth / setWidth) + 2);
    const fragment = document.createDocumentFragment();

    for (let copy = 1; copy < copies; copy++) {
      for (const item of originals) {
        const clone = /** @type {Element} */ (item.cloneNode(true));
        clone.setAttribute('data-marquee-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // A cloned link is still focusable, which would tab into a duplicate.
        for (const focusable of clone.querySelectorAll('a, button, [tabindex]')) {
          focusable.setAttribute('tabindex', '-1');
        }
        for (const identified of clone.querySelectorAll('[id]')) identified.removeAttribute('id');
        clone.removeAttribute('id');
        fragment.append(clone);
      }
    }

    track.append(fragment);

    this.#setWidth = setWidth;
    this.scrollLeft = setWidth + (Number.isFinite(offset) ? offset : 0);
    this.#normalize();
    this.setAttribute('data-ready', '');
  }

  /**
   * Keeps the offset inside [setWidth, 2 * setWidth). Returns the shift that
   * was applied, so a drag in progress can rebase its own origin by the same
   * amount and not snap.
   * @returns {number}
   */
  #normalize() {
    const width = this.#setWidth;
    if (width <= 0) return 0;

    let shift = 0;
    while (this.scrollLeft + shift >= width * 2) shift -= width;
    while (this.scrollLeft + shift < width) shift += width;
    if (shift) this.scrollLeft += shift;
    return shift;
  }

  /** True while the strip should hold still for whoever is looking at it. */
  get #held() {
    return this.#dragging || this.matches(':hover, :focus-within');
  }

  #startLoop() {
    if (this.#loop || !this.isConnected) return;
    this.#lastTick = performance.now();
    this.#loop = requestAnimationFrame(this.#tick);
  }

  #stopLoop() {
    if (this.#loop) cancelAnimationFrame(this.#loop);
    this.#loop = undefined;
  }

  /** @param {number} now */
  #tick = (now) => {
    const elapsed = Math.min(now - this.#lastTick, MAX_FRAME_MS);
    this.#lastTick = now;

    const speed = this.#speed;
    if (speed > 0 && !this.#held && now >= this.#idleUntil) {
      this.#carry += (speed * elapsed) / 1000;
      const step = Math.floor(this.#carry);
      if (step > 0) {
        this.#carry -= step;
        this.scrollLeft += step;
      }
    } else {
      this.#carry = 0;
    }

    this.#normalize();
    this.#loop = requestAnimationFrame(this.#tick);
  };

  #yieldToHand = () => {
    this.#idleUntil = performance.now() + RESUME_DELAY;
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    // Touch is left to the browser: its own momentum beats anything we could
    // hand-roll, and it already scrolls this element natively.
    if (event.pointerType === 'touch' || event.button !== 0) return;
    if (this.#setWidth <= 0) return;

    this.#dragging = true;
    this.#dragPointer = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragStartScroll = this.scrollLeft;
    this.#dragDistance = 0;
    this.setPointerCapture(event.pointerId);
    this.dataset.dragging = '';
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#dragging || event.pointerId !== this.#dragPointer) return;

    const travelled = event.clientX - this.#dragStartX;
    this.#dragDistance = Math.max(this.#dragDistance, Math.abs(travelled));
    this.scrollLeft = this.#dragStartScroll - travelled;
    // Wrapping mid-drag moves the floor under the gesture; move its origin too.
    this.#dragStartScroll += this.#normalize();
  };

  /** @param {PointerEvent} event */
  #onPointerEnd = (event) => {
    if (!this.#dragging || event.pointerId !== this.#dragPointer) return;

    this.#dragging = false;
    this.#dragPointer = -1;
    this.#yieldToHand();
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    delete this.dataset.dragging;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (this.#dragDistance <= DRAG_THRESHOLD) return;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {Event} event */
  #onDragStart = (event) => {
    event.preventDefault();
  };
}

if (!customElements.get('nori-marquee')) {
  customElements.define('nori-marquee', NoriMarquee);
}
